import { type FC, type ReactNode } from "react";

import { clsx } from "clsx";

import { DocumentStatusEnum, type RunMetaFragment } from "@app_schema";

import { track } from "@application/utilities/track";

import { type BadgeColor } from "@styled/badge";
import { FilePill } from "@styled/file_pill";
import { RunMeta } from "@styled/run_meta";

// Subset of the GQL Document shape needed for records-processed pill
// rendering. Stays local (rather than reusing DocumentFragment) because
// pills only need name + status + file metadata — pulling the full
// fragment would over-fetch.
export type RecordDoc = {
  id: string;
  name: string;
  status: DocumentStatusEnum;
  pageCount: number;
  file: { id: string; bytesize: number; type?: string | null };
  // Latest run that processed this document — surfaced admin-only under the
  // pill via RunMeta. Optional so doc sources that don't select it still fit.
  run?: RunMetaFragment | null;
};

// Tone governs the zone-section label color. "upload" = amber (in-progress
// upload state, C1-a/b/c), "complete" = green-800 #166534 (terminal green
// C1-d, matches RSB chrome). Pill colors are driven separately by document
// status, not this tone.
export type ZoneTone = "upload" | "complete";

/**
 * Label + content wrapper for a single zone. Shared between the upload
 * (in-progress) and complete (terminal green) contexts — tone switches the
 * label color only. Consumers control their own inner layout.
 */
export const ZoneSection: FC<{
  tone: ZoneTone;
  label: string;
  children: ReactNode;
}> = ({ tone, label, children }) => (
  <div className="mt-4 first:mt-0">
    <div
      className={clsx(
        "mb-2 text-xs font-bold tracking-wider uppercase",
        tone === "complete" ? "text-emerald-700" : "text-amber-800",
      )}
    >
      {label}
    </div>
    {children}
  </div>
);

/**
 * Zone-variant FilePill: block-width + right-aligned status badge per
 * design `.file-pill`. Upload-tone pills that are still in flight overlay
 * `.file-pill--active` so the eye lands on the doc being processed.
 */
export const RecordFilePill: FC<{
  doc: RecordDoc;
  projectID: string;
  tone: ZoneTone;
  // Substitutes for the page count in the `.fp-pages` slot — used for role
  // labels like "prior input · original medical records" when zone headers
  // are dropped.
  pagesLabel?: string;
}> = ({ doc, projectID, tone, pagesLabel }) => {
  const isInFlight =
    doc.status !== DocumentStatusEnum.Processed &&
    doc.status !== DocumentStatusEnum.Failed;
  return (
    <FilePill
      name={doc.name}
      type={doc.file.type}
      size={doc.file.bytesize}
      pages={pagesLabel ?? doc.pageCount}
      status={recordFileStatusBadge(doc.status, tone)}
      to={`/dashboard/projects/${projectID}/documents/${doc.id}`}
      onView={() =>
        track("document_viewed", {
          project_id: projectID,
          document_id: doc.id,
        })
      }
      block
      active={tone === "upload" && isInFlight}
      meta={doc.run ? <RunMeta run={doc.run} /> : undefined}
    />
  );
};

/**
 * Stack of RecordFilePills with consistent inter-pill spacing. The
 * `mb-2 last:mb-0` wrapper pattern was duplicated in both the upload and
 * complete contexts before this extraction.
 */
export const RecordFilePillStack: FC<{
  docs: RecordDoc[];
  projectID: string;
  tone: ZoneTone;
}> = ({ docs, projectID, tone }) => (
  <>
    {docs.map((doc) => (
      <div key={doc.id} className="mb-2 last:mb-0">
        <RecordFilePill doc={doc} projectID={projectID} tone={tone} />
      </div>
    ))}
  </>
);

/**
 * C1-a/b body. Comp/Supp completion shows every source document as one
 * flat pill stack — no baseline-vs-additional split, since neither
 * project type has a baseline set per spec §4. Single-doc and N-doc
 * projects render the same way (the stack's `last:mb-0` zeroes the
 * trailing margin so a single pill matches the bare-pill layout).
 */
export const ProjectFilePillList: FC<{
  projectID: string;
  sourceDocs: RecordDoc[];
}> = ({ projectID, sourceDocs }) => {
  if (sourceDocs.length === 0) return null;
  return (
    <RecordFilePillStack
      docs={sourceDocs}
      projectID={projectID}
      tone="complete"
    />
  );
};

/**
 * C1-d body — mirrors FollowUpUploadStage's zoned layout in the green
 * complete colorway so the clinician sees the same pills that drove
 * processing, now with Complete badges.
 */
export const FollowUpFilePillList: FC<{
  projectID: string;
  priorReviewDoc: RecordDoc | null;
  baselineSourceDocs: RecordDoc[];
  zone2Docs: RecordDoc[];
}> = ({ projectID, priorReviewDoc, baselineSourceDocs, zone2Docs }) => {
  const zone1Label = priorReviewDoc
    ? "Prior Review Document"
    : "Original Medical Records";

  return (
    <div>
      <ZoneSection tone="complete" label={`1. Prior Records (${zone1Label})`}>
        {priorReviewDoc ? (
          <RecordFilePill
            doc={priorReviewDoc}
            projectID={projectID}
            tone="complete"
          />
        ) : (
          <RecordFilePillStack
            docs={baselineSourceDocs}
            projectID={projectID}
            tone="complete"
          />
        )}
      </ZoneSection>

      <ZoneSection tone="complete" label="2. New Records">
        <RecordFilePillStack
          docs={zone2Docs}
          projectID={projectID}
          tone="complete"
        />
      </ZoneSection>
    </div>
  );
};

// Shared pill status. Processed docs read "Complete"; failed reads
// "Failed". In the upload tone, pre-processing statuses (initialized /
// queueing) read "Queued" while active processing reads "Processing".
// In the complete tone (terminal green C1-d) any non-failed doc reads
// "Complete" since the whole record review has already resolved.
function recordFileStatusBadge(
  status: DocumentStatusEnum,
  tone: ZoneTone,
): { text: string; color: BadgeColor } {
  if (status === DocumentStatusEnum.Failed) {
    return { text: "Failed", color: "rose" };
  }
  if (status === DocumentStatusEnum.Processed || tone === "complete") {
    return { text: "Complete", color: "emerald" };
  }
  if (status === DocumentStatusEnum.Processing) {
    return { text: "Processing", color: "indigo" };
  }
  return { text: "Queued", color: "slate" };
}
